import React, { useState } from "react";
import PropTypes from "prop-types";
// eslint-disable-next-line import/no-cycle
import { CardImage } from "../../../../sharedComponents/Card/Image/CardImage";
import * as styles from "./CardSlider.module.scss";

const defaultLabels = {
	prevSlideLabel: "Previous slide",
	nextSlideLabel: "Next slide",
	paginationBulletLabel: "Go to slide {{index}}",
};

const CardSlider = ({ images, sliderNav, labels: providedLabels, ratio }) => {
	const labels = { ...defaultLabels, ...providedLabels };
	const [activeSlide, setActiveSlide] = useState(0);
	return (
		<div className={styles.slider} data-gallery={images?.length > 1}>
			<span className={styles.slide__arrow} />
			<div className={styles.slides} style={ratio ? { "--image-ratio": `${ratio}` } : null}>
				{images?.map((img, index) => {
					const firstSlide = index === 0;
					const lastSlide = images.length - 1;
					return (
						// eslint-disable-next-line jsx-a11y/no-static-element-interactions
						<div
							key={img.id || index}
							id={`slides__${index}`}
							data-image="gallery"
							data-active={index === activeSlide}
							// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
							tabIndex={0}
							onKeyDown={e => {
								if (e.keyCode === 37) {
									setActiveSlide(activeSlide === 0 ? lastSlide : slideState => slideState - 1);
								}
								if (e.keyCode === 39) {
									setActiveSlide(activeSlide < lastSlide ? slideState => slideState + 1 : 0);
								}
							}}
							className={styles.slide}>
							<CardImage
								className={styles.slide__image}
								src={!img.srcSet && img.src}
								source={img.srcSet && img}
								alt={img.alt}
								fluid={img.fluid}
							/>
							{images.length > 1 && (
								<>
									<a
										className={styles.slide__prev}
										onClick={() => setActiveSlide(firstSlide ? lastSlide : index - 1)}
										tabIndex={activeSlide === index ? 0 : -1}
										href={`#slides__${firstSlide ? lastSlide : index - 1}`}
										title={labels.prevSlideLabel}>
										<span className={styles.slide__sr}>{labels.prevSlideLabel}</span>
									</a>
									<a
										className={styles.slide__next}
										onClick={() => setActiveSlide(index < lastSlide ? index + 1 : 0)}
										href={`#slides__${index < lastSlide ? index + 1 : 0}`}
										tabIndex={activeSlide === index ? 0 : -1}
										title={labels.nextSlideLabel}>
										<span className={styles.slide__sr}>{labels.nextSlideLabel}</span>
									</a>
								</>
							)}
						</div>
					);
				})}
			</div>
			{sliderNav && images.length > 1 && (
				<div className={styles.sliderNav}>
					{images?.map((_, index) => (
						<a
							className={styles.sliderNavLink}
							tabIndex="-1"
							data-active={index === activeSlide}
							title={labels.paginationBulletLabel.replace("{{index}}", index + 1)}
							onClick={() => setActiveSlide(index)}
							href={`#slides__${index}`}>
							<span className={styles.slide__sr}>
								{labels.paginationBulletLabel.replace("{{index}}", index)}
							</span>
						</a>
					))}
				</div>
			)}
		</div>
	);
};

CardSlider.propTypes = {
	images: PropTypes.arrayOf(
		PropTypes.shape({
			fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
			source: PropTypes.shape({
				src: PropTypes.string,
				srcSet: PropTypes.string,
				sizes: PropTypes.string,
			}),
			alt: PropTypes.string,
			src: PropTypes.string,
		})
	),
	ratio: PropTypes.string,
	labels: PropTypes.shape({
		prevSlideLabel: PropTypes.string,
		nextSlideLabel: PropTypes.string,
		paginationBulletLabel: PropTypes.string,
	}),
	sliderNav: PropTypes.bool,
};

CardSlider.defaultProps = {
	images: [],
	ratio: undefined,
	labels: defaultLabels,
	sliderNav: true,
};

export default CardSlider;
export { CardSlider };
